"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { sendContactEmail } from "@/app/actions";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MapPin, CheckCircle2, AlertCircle } from "@/lib/icons";
import Image from "next/image";
import { Checkbox } from "@/components/ui/checkbox";
import Link from "next/link";

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  company: z.string().optional(),
  service: z.string().min(1, "Please select a service"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must agree to the privacy policy to submit this form",
  }),
});

type FormData = z.infer<typeof formSchema>;

export function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      service: "",
      message: "",
      consent: false,
    },
  });

  const onSubmit = async (data: FormData) => {
    console.log('=== Form Submission Started ===');
    console.log('Form data:', data);
    
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      console.log('Calling server action...');
      const result = await sendContactEmail(data);
      console.log('Server action result:', result);

      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your enquiry! We\'ll get back to you within 24 hours.',
        });
        form.reset();
        
        // Clear success message after 10 seconds
        setTimeout(() => {
          setSubmitStatus({ type: null, message: '' });
        }, 10000);
      } else {
        throw new Error(result.error || 'Failed to send enquiry');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error 
          ? error.message 
          : 'Something went wrong. Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ContactForm = () => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7">
        {/* Success/Error Message */}
        {submitStatus.type && (
          <div
            className={`p-5 rounded-xl flex items-start gap-4 ${
              submitStatus.type === 'success'
                ? 'bg-green-50/80 border border-green-200/60'
                : 'bg-red-50/80 border border-red-200/60'
            }`}
            role="alert"
          >
            {submitStatus.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <p
              className={`text-sm leading-relaxed ${
                submitStatus.type === 'success' ? 'text-green-900' : 'text-red-900'
              }`}
            >
              {submitStatus.message}
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Name *</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} className="h-12" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Email *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="h-12" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Phone *</FormLabel>
                <FormControl>
                  <Input placeholder="01737 647 280" {...field} className="h-12" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Company</FormLabel>
                <FormControl>
                  <Input placeholder="Your Company Ltd" {...field} className="h-12" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">Service Required *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="rental">Equipment Rental</SelectItem>
                  <SelectItem value="sales">Equipment Sales</SelectItem>
                  <SelectItem value="installation">Installation</SelectItem>
                  <SelectItem value="maintenance">Maintenance Contract</SelectItem>
                  <SelectItem value="repair">Emergency Repair</SelectItem>
                  <SelectItem value="consultancy">Consultancy</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">Message *</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Tell us about your requirements..."
                  className="resize-none min-h-[140px]"
                  rows={5}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="consent"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal text-gray-700 cursor-pointer">
                  I agree to DPCLR processing my information in accordance with the{' '}
                  <Link href="/privacy" className="text-blue-600 hover:text-blue-700 underline">
                    Privacy Policy
                  </Link>
                  {' '}*
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          className="w-full h-13 text-base font-medium bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Sending...
            </span>
          ) : (
            'Send Enquiry'
          )}
        </Button>
      </form>
    </Form>
  );

  return (
    <section id="contact" className="relative py-32 px-4 overflow-hidden">
      {/* Modern background with depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/5 rounded-full blur-3xl" />
      </div>
      
      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className="max-w-3xl mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-blue-700">Quick Response Team</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
            Let&apos;s Talk Laundry
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Whether you&apos;re looking to rent, buy, or just get expert advice, we&apos;re here to help.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Methods - Cleaner, more spacious */}
          <div className="lg:col-span-2 space-y-5">
            {/* Phone */}
            <a 
              href="tel:+441737647280"
              className="group block p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <Phone className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-500 mb-1">Phone</div>
                  <div className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    01737 647 280
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Mon-Fri, 8am-6pm</div>
                </div>
              </div>
            </a>

            {/* Email */}
            <div className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-500 mb-1">Email</div>
                  <div className="space-y-1">
                    <a href="mailto:sales@dpclr.co.uk" className="block text-base font-medium text-gray-900 hover:text-blue-600 transition-colors">
                      sales@dpclr.co.uk
                    </a>
                    <a href="mailto:service@dpclr.co.uk" className="block text-base font-medium text-gray-900 hover:text-blue-600 transition-colors">
                      service@dpclr.co.uk
                    </a>
                  </div>
                  <div className="text-sm text-gray-600 mt-2">Response within 24 hours</div>
                </div>
              </div>
            </div>

            {/* Coverage */}
            <div className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-500 mb-1">Service Area</div>
                  <div className="text-base font-medium text-gray-900">
                    North, Midlands, South East & South West England
                  </div>
                  <div className="text-sm text-gray-600 mt-2">Emergency callouts available</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form - Modern, clean design */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-200/50 p-8 lg:p-10 shadow-sm">
              <ContactForm />
            </div>
          </div>
        </div>

        {/* Certification Logos - Modern, cleaner layout */}
        <div className="mt-32 pt-12">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1.5 bg-gray-100 rounded-full mb-3">
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Accredited & Certified</span>
            </div>
            <p className="text-sm text-gray-600">Trusted by industry leaders and certified professionals</p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-16 opacity-70 hover:opacity-100 transition-opacity duration-300">
            <div className="group flex flex-col items-center gap-4">
              <div className="relative w-[140px] h-[60px] grayscale group-hover:grayscale-0 transition-all duration-300">
                <Image
                  src="https://images.prismic.io/forbes-pro/aJy5sKTt2nPbaSbc_SafeContractor-logo.avif?auto=format,compress"
                  alt="SafeContractor (Alcumus) Approved"
                  fill
                  className="object-contain"
                  sizes="140px"
                />
              </div>
              <p className="text-xs text-gray-500 font-medium">SafeContractor Approved</p>
            </div>
            <div className="group flex flex-col items-center gap-4">
              <div className="relative w-[120px] h-[60px] grayscale group-hover:grayscale-0 transition-all duration-300">
                <Image
                  src="https://images.prismic.io/forbes-pro/aJy5dKTt2nPbaSbM_gas-safe-register-logo.png?auto=format,compress"
                  alt="Gas Safe Registered"
                  fill
                  className="object-contain"
                  sizes="120px"
                />
              </div>
              <p className="text-xs text-gray-500 font-medium">Gas Safe Registered</p>
            </div>
            <div className="group flex flex-col items-center gap-4">
              <div className="relative w-[100px] h-[60px] grayscale group-hover:grayscale-0 transition-all duration-300">
                <Image
                  src="https://images.prismic.io/forbes-pro/aJy5L6Tt2nPbaSa-_corgi-logo.png?auto=format,compress"
                  alt="CORGI Certified (Legacy)"
                  fill
                  className="object-contain"
                  sizes="100px"
                />
              </div>
              <p className="text-xs text-gray-500 font-medium">CORGI Certified</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
